import React, { useEffect, useState } from 'react'
import {FileText, Search, Filter} from 'lucide-react'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import AdminLayout from '../../components/admin/AdminLayout'

const AdminLogs: React.FC = () => {
  const { data: logs, loading, fetchData } = useEntityCRUD('admin_logs')
  const [searchTerm, setSearchTerm] = useState('')
  const [actionFilter, setActionFilter] = useState<string>('ALL')

  useEffect(() => {
    const filter: any = {}
    if (actionFilter !== 'ALL') {
      filter.action = actionFilter
    }
    fetchData({ filter, sort: { createdAt: -1 }, limit: 100 })
  }, [actionFilter])

  const filteredLogs = logs.filter(log =>
    log.adminName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.entityType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.details?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getActionBadge = (action: string) => {
    const styles = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
      APPROVE: 'bg-green-100 text-green-800',
      REJECT: 'bg-red-100 text-red-800',
      PROMOTE: 'bg-purple-100 text-purple-800',
      REVOKE: 'bg-orange-100 text-orange-800'
    }
    return styles[action as keyof typeof styles] || 'bg-gray-100 text-gray-800'
  }

  const getActionLabel = (action: string) => {
    const labels = {
      CREATE: 'Criação',
      UPDATE: 'Atualização',
      DELETE: 'Exclusão',
      APPROVE: 'Aprovação',
      REJECT: 'Rejeição',
      PROMOTE: 'Promoção',
      REVOKE: 'Revogação'
    }
    return labels[action as keyof typeof labels] || action
  }

  const getEntityLabel = (entityType: string) => {
    const labels = {
      prayers: 'Orações',
      testimonials: 'Depoimentos',
      sermons: 'Sermões',
      events: 'Eventos',
      worship_songs: 'Louvores',
      bible_school_classes: 'Escola Bíblica',
      kids_comics: 'Kids',
      contacts: 'Contatos',
      users: 'Usuários'
    }
    return labels[entityType as keyof typeof labels] || entityType
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Logs Administrativos</h1>
        <p className="text-gray-600">Histórico de ações realizadas por administradores</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por admin, entidade ou detalhes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
          >
            <option value="ALL">Todas as Ações</option>
            <option value="CREATE">Criação</option>
            <option value="UPDATE">Atualização</option>
            <option value="DELETE">Exclusão</option>
            <option value="APPROVE">Aprovação</option>
            <option value="REJECT">Rejeição</option>
            <option value="PROMOTE">Promoção</option>
            <option value="REVOKE">Revogação</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLogs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Nenhum log encontrado</p>
            </div>
          ) : (
            filteredLogs.map((log: any) => (
              <div key={log._id} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionBadge(log.action)}`}>
                        {getActionLabel(log.action)}
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        {getEntityLabel(log.entityType)}
                      </span>
                      <span className="text-xs text-gray-400">
                        por <span className="font-medium text-gray-600">{log.adminName}</span>
                      </span>
                    </div>
                    {log.details && (
                      <p className="text-sm text-gray-600 ml-1">{log.details}</p>
                    )}
                    <p className="text-xs text-gray-400 ml-1 mt-1">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminLogs